import { useTranslation } from 'react-i18next';
import { Separator } from '@/components/ui/separator';
import { formatProductPrice } from '@/lib/productPrice';
import { useEntwurf } from '@/entwurf/EntwurfOptionen';
import { splitAtCut } from '@/lib/titleCut';
import { ShoppingTile } from './ShoppingTile';
import { ScorePanel } from './ScorePanel';
import type { WorkbenchProduct, WorkbenchEditState } from '@/hooks/useWorkbench';

interface CustomerColumnProps {
  product: WorkbenchProduct;
  editState: WorkbenchEditState;
  /**
   * Vereinigung aus "schon gespeichert" und "gerade getippt"; gebildet im
   * `WorkbenchReviewer`, weil nur dort beide Haelften vorliegen.
   */
  hasManualEdit?: boolean;
}

/**
 * Linke Spalte: was der Kunde sieht.
 *
 * Sie faehrt mit dem Rest der Karte mit (seit T6.1, 08.09.2026: EIN
 * Scrollbereich statt zwei) und bleibt trotzdem der Bezugspunkt, waehrend
 * rechts der Feed-Inhalt durchgegangen wird: so kommt es an, und so viele
 * Punkte bringt es. Die Spalte ist so bemessen, dass sie ab etwa 800 px
 * Fensterhoehe vollstaendig ohne Scrollen passt.
 */
export function CustomerColumn({ product, editState, hasManualEdit = false }: CustomerColumnProps) {
  const { t, i18n } = useTranslation('workbench');
  const { kuerzungsHinweisLinks, punkteOrt } = useEntwurf();
  /* CODEPUNKTE, nicht `title.length`. Dieselbe Rechnung wie der Strich in der
     Kuerzungsmarke - sonst nennen zwei Stellen auf einer Karte verschiedene
     Zahlen fuer dieselbe Grenze. */
  const schnitt = splitAtCut(editState.optimized_title);
  const gesamtZeichen = Array.from(editState.optimized_title).length;

  return (
    <div className="flex flex-col gap-3">
      <ShoppingTile
        imageLink={product.image_link}
        /* Spec Original-Titel 5.4: nie product.title. Die Kachel blendet ein leeres Vorher aus. */
        beforeTitle={product.original_title ?? ''}
        /* Spec 8: aus dem Bearbeitungsstand, nicht aus product. */
        afterTitle={editState.optimized_title}
        price={formatProductPrice(product.price, product.currency, i18n?.language)}
        link={product.link}
        isManualEdit={hasManualEdit}
      />

      <p className="text-[10px] leading-tight text-muted-foreground">
        {kuerzungsHinweisLinks === 'satz'
          ? t('tile.cutHint')
          : schnitt.isCut
            ? t('tile.cutCountLabeled', {
                visible: Array.from(schnitt.visible).length,
                total: gesamtZeichen,
              })
            : t('tile.cutNoneLabeled')}
      </p>

      {punkteOrt === 'kachel' && (
        /* `imAenderungsblock` zieht die Punktzahl in den Aenderungsblock der
           rechten Spalte um (FeedColumn) - hier stuende sie sonst doppelt. */
        <>
          <Separator />
          <ScorePanel
            post={product.componentScores}
            postTotal={product.totalScore}
            pre={product.preComponentScores}
            preTotal={product.preTotalScore}
            scoreType={product.scoreType}
            attributeApplicable={product.attributeApplicable}
            preCalculatedAt={product.preCalculatedAt}
            postCalculatedAt={product.postCalculatedAt}
          />
        </>
      )}
    </div>
  );
}
